/**
 * オーバーレイの描画ハンドラレジストリ（EP-2）。
 *
 * 「type ＋ 正規化矩形(0–1) ＋ payload」1 モデルの上に、type ごとの描き手を差し込む。
 * `app.overlays.registerRenderer(type, fn)` が v1 の公開拡張点（EP-8 は座席のまま・
 * 公開するのはツールバーのボタン追加とここだけ）。
 *
 * 未登録 type のオーバーレイは**黙って無視する** — サーバが将来の type を配ってきても
 * 古いクライアントが壊れないようにするため（前方互換）。
 */
import type { Translate } from '../i18n/i18n';
import type { ViewerApi } from '../ui/api';

/** サーバから配られるオーバーレイ 1 件（配信 API の item）。 */
export interface OverlayItem {
  id: number;
  page: number;
  type: string;
  /** ページ矩形に対する 0–1 正規化座標（左上原点・W3C xywh=percent 互換）。 */
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  payload: Record<string, unknown>;
}

/** renderer が使える文脈（ビューワ操作・文言・別ブックの URL 解決・フォーム送信）。 */
export interface OverlayContext {
  doc: Document;
  api: ViewerApi;
  translate: Translate;
  /** 別ブックの特定ページを指す絶対 URL（導けなければ null）。 */
  bookUrl(slug: string, page: number): string | null;
  /** フォーム送信先の API ベース（`…/api/v1`）。導けなければ null（送信不可）。 */
  apiBase: string | null;
  /** このブックの slug（フォーム送信の本文に載せる）。 */
  bookSlug: string;
  /**
   * ゲート通過後に呼ぶ。案内を告げ、**反映（読み込み直し）を行う関数を返す**。
   * 反映を自動で走らせず呼び出し側へ渡すのは、時間で勝手に読み込み直すと利用者が
   * 止める手段の無い制限時間になるため（WCAG 2.2.1）。呼び出し側は利用者の操作に結び付ける。
   */
  unlocked(): () => void;
}

/**
 * renderer が返す制御ハンドル。
 * - `dispose` は層の後始末（M7a の `() => void` と等価）。
 * - `visibilityChanged` は要素が表示中ページから外れた／戻ったときに呼ばれる
 *   （メディアの一時停止に使う）。**めくり中の一時退避では呼ばれない** —
 *   静止して「別ページになった」と確定したときだけ。
 */
export interface OverlayHandle {
  dispose?(): void;
  visibilityChanged?(visible: boolean): void;
}

/**
 * 描き手。渡された空の要素（矩形は層が配置する）を埋める。
 * 後始末や可視通知が要る場合だけ、関数（＝dispose のみ）かハンドルを返す。
 */
export type OverlayRenderer = (
  el: HTMLElement,
  item: OverlayItem,
  context: OverlayContext,
) => (() => void) | OverlayHandle | void;

export class OverlayRegistry {
  private readonly renderers = new Map<string, OverlayRenderer>();

  /** type に描き手を登録する（同じ type の再登録は上書き）。 */
  registerRenderer(type: string, renderer: OverlayRenderer): void {
    this.renderers.set(type, renderer);
  }

  /** 登録済みの描き手（無ければ undefined＝その type は描かない）。 */
  rendererFor(type: string): OverlayRenderer | undefined {
    return this.renderers.get(type);
  }
}
